import { useState } from 'react'

import { Box, Item } from '@factorialco/deprecated-design-system'
import { TaxTypeFragment } from 'generated/resources/graphql'
import { compact } from 'lodash'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

function formatTaxTypeName(taxType?: { name?: string | null; countryCode?: string | null } | null) {
  if (!taxType) return ''
  return compact([taxType?.name ?? '', taxType?.countryCode?.toUpperCase() ?? '']).join(' - ')
}

interface TaxTypeSelectorProps {
  currentTaxTypeId?: string | null
  taxTypes?: TaxTypeFragment[]
  disabled: boolean
  onSelect: (taxType?: TaxTypeFragment) => void
  countryCode?: string
}

export const TaxTypeSelector = ({
  currentTaxTypeId,
  disabled,
  onSelect,
  taxTypes,
  countryCode,
}: TaxTypeSelectorProps) => {
  const [selectedTaxTypeId, setSelectedTaxTypeId] = useState<string | undefined | null>(
    currentTaxTypeId
  )

  const taxTypesList = useUnsafeMemo(() => {
    if (!taxTypes) return []

    if (!!countryCode && countryCode !== '-') {
      const filteredByCountry = taxTypes.filter(
        (taxType) =>
          taxType.countryCode?.toLowerCase() === countryCode.toLowerCase() ||
          taxType.countryCode === null ||
          taxType.countryCode === undefined
      )
      return filteredByCountry.length > 0 ? filteredByCountry : taxTypes
    }

    return taxTypes
  }, [taxTypes, countryCode])

  const selectedTaxType = useUnsafeMemo(
    () => taxTypesList?.find((a) => a.id === selectedTaxTypeId),
    [taxTypesList, selectedTaxTypeId]
  )

  const onSelectInterceptor = useUnsafeCallback(
    (item: Item) => {
      const id = String(item.value)
      setSelectedTaxTypeId(id)
      const newTaxType = taxTypesList?.find((a) => a.id == id)
      onSelect(newTaxType)
    },
    [onSelect, taxTypesList]
  )

  const taxOptions = useUnsafeMemo(() => {
    return taxTypesList.map((o) => ({
      label: formatTaxTypeName(o),
      value: o.id,
    }))
  }, [taxTypesList])

  return (
    <Box>
      <DropdownSearchBar
        name={formatTaxTypeName(selectedTaxType)}
        data={taxOptions || []}
        label={i18n.t('expenses_v2.forms.fields.taxes.label_tax_type')}
        labelNotFound={i18n.t('expenses_v2.forms.fields.taxes.label_tax_type_not_found')}
        searchLabel={i18n.t('common.search')}
        sortLabel={i18n.t('common.name')}
        placeholder={i18n.t('expenses_v2.forms.fields.taxes.label_tax_type')}
        selectLabel={i18n.t('expenses_v2.forms.fields.taxes.label_tax_type_select')}
        disabled={disabled}
        autofocus={false}
        loading={false}
        onSelect={onSelectInterceptor}
      />
    </Box>
  )
}
